import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ShieldCheck, Loader2, RefreshCw, LogOut, FileWarning, ClipboardList,
  CreditCard, Activity, CheckCircle2, XCircle, Server, Lock,
  Download, Flame, Package,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import { adminGetCompliance, adminFetchAuditPack } from '../services/api';

const ADMIN_KEY_STORAGE = 'orosz_admin_key_v1';
const TABS = [
  { id: 'overview',    label: 'Áttekintés',  icon: Activity },
  { id: 'withdrawals', label: 'Elállások',    icon: FileWarning },
  { id: 'anamnesis',   label: 'Anamnézis',    icon: ClipboardList },
  { id: 'payments',    label: 'Fizetések',    icon: CreditCard },
];

const fmtHuDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('hu-HU', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const fmtHuf = (n) => {
  const v = Number(n || 0);
  return `${v.toLocaleString('hu-HU')} Ft`;
};

const COACH_TYPE_LABEL = {
  online:          '🌐 Online',
  offline_miskolc: '🏛️ Miskolc',
};

// ---- Login screen --------------------------------------------------------
const LoginScreen = ({ onAuth }) => {
  const [key, setKey] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!key.trim()) return;
    setSubmitting(true);
    try {
      await adminGetCompliance(key.trim(), 1);
      localStorage.setItem(ADMIN_KEY_STORAGE, key.trim());
      onAuth(key.trim());
      toast.success('Bejelentkezve.');
    } catch (_err) {
      toast.error('Érvénytelen admin kulcs.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-6">
      <form
        onSubmit={handleSubmit}
        data-testid="compliance-login-form"
        className="w-full max-w-md bg-gray-800/80 backdrop-blur-md rounded-2xl p-8 border border-gray-700 shadow-2xl"
      >
        <div className="flex justify-center mb-6">
          <div className="w-14 h-14 rounded-full bg-yellow-600/20 flex items-center justify-center">
            <ShieldCheck className="text-yellow-500" size={28} />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-center mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Compliance Footprint
        </h1>
        <p className="text-sm text-gray-400 text-center mb-6">
          Egyetlen képernyős audit-trail — Barion live-audit ready.
        </p>
        <Input
          data-testid="compliance-key-input"
          type="password"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="ADMIN_API_KEY"
          className="bg-gray-900 border-gray-700 text-white mb-4"
        />
        <Button
          data-testid="compliance-login-btn"
          type="submit"
          disabled={submitting || !key.trim()}
          className="w-full bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold"
        >
          {submitting ? <><Loader2 className="mr-2 animate-spin" size={16} /> Belépés…</> : <><Lock className="mr-2" size={16} /> Belépés</>}
        </Button>
      </form>
    </div>
  );
};

// ---- KPI tile ------------------------------------------------------------
const KpiTile = ({ label, value, sub, accent = 'yellow', testid }) => {
  const accents = {
    yellow: 'border-yellow-500/30 from-yellow-500/10 to-yellow-600/5',
    red:    'border-red-500/30 from-red-500/10 to-red-600/5',
    green:  'border-emerald-500/30 from-emerald-500/10 to-emerald-600/5',
    blue:   'border-sky-500/30 from-sky-500/10 to-sky-600/5',
  };
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border bg-gradient-to-br ${accents[accent]} p-5`}
    >
      <div className="text-[10px] font-bold tracking-widest uppercase text-gray-400">{label}</div>
      <div className="text-3xl font-bold text-white mt-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
        {Number(value || 0).toLocaleString('hu-HU')}
      </div>
      {sub && <div className="text-[11px] text-gray-400 mt-1">{sub}</div>}
    </div>
  );
};

// ---- Dashboard -----------------------------------------------------------
const Dashboard = ({ adminKey, onLogout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await adminGetCompliance(adminKey, 30);
      setData(d);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error('Lejárt admin kulcs.');
        onLogout();
        return;
      }
      setError(err?.response?.data?.detail || err.message || 'Betöltési hiba.');
    } finally {
      setLoading(false);
    }
  }, [adminKey, onLogout]);

  useEffect(() => { load(); }, [load]);

  // Frontend-only Barion Pixel state (since the ID lives in REACT_APP_*).
  const pixelState = useMemo(() => {
    if (typeof window === 'undefined') return { configured: false, id: null };
    const id = window.barion_pixel_id || null;
    const configured = !!(id && typeof window.bp === 'function');
    return { configured, id };
  }, []);

  const exportJson = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compliance-footprint-${new Date().toISOString().slice(0,10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const [downloadingPack, setDownloadingPack] = useState(false);
  const downloadAuditPack = async () => {
    setDownloadingPack(true);
    try {
      const blob = await adminFetchAuditPack(adminKey);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orosz-coaching-audit-pack-${new Date().toISOString().slice(0,10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Audit Pack ZIP letöltve.');
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error('Lejárt admin kulcs.');
        onLogout();
      } else {
        toast.error('Audit Pack letöltése sikertelen.');
      }
    } finally {
      setDownloadingPack(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="bg-gray-900/80 backdrop-blur-md border-b border-gray-800 sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-yellow-500/15 grid place-items-center shrink-0">
              <ShieldCheck size={20} className="text-yellow-500" />
            </div>
            <div className="min-w-0">
              <h1 className="text-base md:text-lg font-bold truncate" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Compliance Footprint
              </h1>
              <p className="text-[11px] text-gray-400 truncate">
                {data?.generated_at ? `Frissítve: ${fmtHuDate(data.generated_at)}` : 'Audit trail – Barion live ready'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="compliance-refresh-btn"
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              className="bg-transparent border-gray-700 text-gray-200 hover:bg-gray-800"
            >
              <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Frissítés
            </Button>
            <Button
              data-testid="compliance-audit-pack-btn"
              size="sm"
              onClick={downloadAuditPack}
              disabled={downloadingPack || !data}
              className="bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-bold shadow-lg shadow-yellow-500/20"
            >
              {downloadingPack
                ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Generálás…</>
                : <><Package size={14} className="mr-1.5" /> Audit Pack ZIP</>
              }
            </Button>
            <Button
              data-testid="compliance-export-btn"
              variant="outline"
              size="sm"
              onClick={exportJson}
              disabled={!data}
              className="bg-transparent border-gray-700 text-gray-200 hover:bg-gray-800"
            >
              <Download size={14} className="mr-1.5" /> Export JSON
            </Button>
            <Button
              data-testid="compliance-logout-btn"
              variant="outline"
              size="sm"
              onClick={onLogout}
              className="bg-transparent border-gray-700 text-gray-200 hover:bg-gray-800"
            >
              <LogOut size={14} className="mr-1.5" /> Kilépés
            </Button>
          </div>
        </div>

        {/* Tab bar */}
        <div className="container mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`compliance-tab-${t.id}`}
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap transition-colors border-b-2 ${
                  active
                    ? 'border-yellow-500 text-yellow-400'
                    : 'border-transparent text-gray-400 hover:text-gray-200'
                }`}
              >
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-8 max-w-7xl">
        {loading && !data && (
          <div className="flex items-center justify-center py-20 text-gray-400">
            <Loader2 className="animate-spin mr-2" size={20} /> Adatok betöltése…
          </div>
        )}
        {error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300" data-testid="compliance-error">
            {error}
          </div>
        )}

        {data && activeTab === 'overview' && (
          <div data-testid="compliance-overview">
            <h2 className="text-xs font-bold tracking-widest uppercase text-gray-400 mb-3">Compliance KPI-k (utolsó 30 nap)</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              <KpiTile testid="kpi-withdrawals" label="Elállás (30d)" value={data.withdrawals.counts.last_30d} sub={`Összes: ${data.withdrawals.counts.total}`} accent="red" />
              <KpiTile testid="kpi-anamnesis"   label="Anamnézis (30d)" value={data.anamnesis.counts.last_30d}   sub={`Összes: ${data.anamnesis.counts.total}`} accent="yellow" />
              <KpiTile testid="kpi-payments-paid" label="Sikeres fizetés (30d)" value={data.payments.counts_paid.last_30d} sub={`Összes sikeres: ${data.payments.counts_paid.total}`} accent="green" />
              <KpiTile testid="kpi-payments-all" label="Összes fizetés-kísérlet (30d)" value={data.payments.counts_all.last_30d} sub={`Összes: ${data.payments.counts_all.total}`} accent="blue" />
            </div>

            <h2 className="text-xs font-bold tracking-widest uppercase text-gray-400 mb-3 flex items-center gap-2">
              <Server size={12} /> Rendszer állapot
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
              <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-5 space-y-3" data-testid="compliance-system-card">
                <SystemRow label="Barion környezet"      value={data.system.barion_env} highlight={data.system.barion_env === 'prod'} />
                <SystemRow label="Barion POS kulcs"      ok={data.system.barion_pos_key_set} />
                <SystemRow label="Számlázz.hu Agent"     ok={data.system.szamlazz_agent_key_set} />
                <SystemRow label="Resend (e-mail)"       ok={data.system.resend_api_key_set}
                           note={!data.system.resend_api_key_set ? 'MOCK módban' : null} />
                <SystemRow label="Admin API kulcs"       ok={data.system.admin_api_key_set} />
              </div>
              <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-5 space-y-3" data-testid="compliance-pixel-card">
                <div className="flex items-center gap-2 text-sm">
                  <Flame size={14} className="text-yellow-500" />
                  <span className="font-bold">Barion Pixel</span>
                </div>
                <SystemRow label="Pixel ID konfigurálva" ok={pixelState.configured} />
                <SystemRow label="Pixel ID"              value={pixelState.id || '— (nincs beállítva)'} />
                <p className="text-[11px] text-gray-500 leading-relaxed">
                  A Pixel ID a <code className="bg-gray-800 px-1 py-0.5 rounded">REACT_APP_BARION_PIXEL_ID</code> build-time környezeti változóból
                  származik. Ha üres, a Base + Full Pixel safe no-op módban van; aktiváláshoz add meg az élő (BP-…) azonosítót és deploy-olj újra.
                </p>
              </div>
            </div>
          </div>
        )}

        {data && activeTab === 'withdrawals' && (
          <DataTable
            testid="compliance-withdrawals-table"
            title="Elállási nyilatkozatok (utolsó 30)"
            empty="Még nem érkezett elállási nyilatkozat."
            columns={[
              { key: 'created_at',   label: 'Beérkezett', render: (v) => fmtHuDate(v) },
              { key: 'name',         label: 'Név' },
              { key: 'email',        label: 'E-mail',     render: (v) => (
                  <a href={`mailto:${v}`} className="text-yellow-400 hover:text-yellow-300 break-all">{v}</a>
                ) },
              { key: 'order_number', label: 'Rendelésszám', mono: true },
              { key: 'purchase_date',label: 'Vásárlás dátuma' },
              { key: 'status',       label: 'Státusz' },
            ]}
            rows={data.withdrawals.recent}
          />
        )}

        {data && activeTab === 'anamnesis' && (
          <DataTable
            testid="compliance-anamnesis-table"
            title="Anamnézis kérdőívek (utolsó 30)"
            empty="Még nem érkezett anamnézis kitöltés."
            columns={[
              { key: 'created_at',    label: 'Beérkezett', render: (v) => fmtHuDate(v) },
              { key: 'name',          label: 'Név' },
              { key: 'email',         label: 'E-mail', render: (v) => (
                  <a href={`mailto:${v}`} className="text-yellow-400 hover:text-yellow-300 break-all">{v}</a>
                ) },
              { key: 'coaching_type', label: 'Típus', render: (v) => COACH_TYPE_LABEL[v] || v },
              { key: 'primary_goal',  label: 'Elsődleges cél' },
              { key: 'consent_all',   label: 'Beleegyezések', render: (v) => v
                  ? <span className="inline-flex items-center gap-1 text-emerald-400 text-xs"><CheckCircle2 size={12} /> Mind</span>
                  : <span className="inline-flex items-center gap-1 text-red-400 text-xs"><XCircle size={12} /> Hiányos</span>
                },
            ]}
            rows={data.anamnesis.recent}
          />
        )}

        {data && activeTab === 'payments' && (
          <DataTable
            testid="compliance-payments-table"
            title="Fizetések (utolsó 30)"
            empty="Még nem érkezett fizetési kísérlet."
            columns={[
              { key: 'created_at',    label: 'Indítva', render: (v) => fmtHuDate(v) },
              { key: 'customer_name', label: 'Vásárló' },
              { key: 'customer_email',label: 'E-mail', render: (v) => v ? (
                  <a href={`mailto:${v}`} className="text-yellow-400 hover:text-yellow-300 break-all">{v}</a>
                ) : '—' },
              { key: 'package_name',  label: 'Csomag' },
              { key: 'amount_huf',    label: 'Összeg', render: (v) => fmtHuf(v) },
              { key: 'status',        label: 'Státusz', render: (v) => {
                  const cls = v === 'succeeded' ? 'text-emerald-400'
                            : v === 'failed'    ? 'text-red-400'
                            : 'text-gray-300';
                  return <span className={cls + ' font-semibold'}>{v}</span>;
                }
              },
              { key: 'source',        label: 'Forrás', render: (v) => v || 'direct' },
              { key: 'environment',   label: 'Env' },
              { key: 'order_id',      label: 'Order ID', mono: true, render: (v) => (v || '').slice(0, 10) + '…' },
            ]}
            rows={data.payments.recent}
          />
        )}
      </main>
    </div>
  );
};

const SystemRow = ({ label, ok, value, note, highlight }) => (
  <div className="flex items-center justify-between gap-3 text-sm">
    <span className="text-gray-400">{label}</span>
    {value !== undefined ? (
      <span className={`font-mono text-xs ${highlight ? 'text-yellow-400 font-bold' : 'text-gray-200'}`}>{value}</span>
    ) : ok ? (
      <span className="inline-flex items-center gap-1.5 text-emerald-400 text-xs font-semibold">
        <CheckCircle2 size={14} /> OK
      </span>
    ) : (
      <span className="inline-flex items-center gap-1.5 text-red-400 text-xs font-semibold">
        <XCircle size={14} /> {note || 'Hiányzik'}
      </span>
    )}
  </div>
);

const DataTable = ({ testid, title, columns, rows, empty }) => (
  <div data-testid={testid}>
    <h2 className="text-xs font-bold tracking-widest uppercase text-gray-400 mb-3">{title}</h2>
    <div className="overflow-x-auto rounded-xl border border-gray-800 bg-gray-900/50">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-800 text-left text-[10px] font-bold tracking-widest uppercase text-gray-500">
            {columns.map((c) => (
              <th key={c.key} className="px-4 py-3 whitespace-nowrap">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(!rows || rows.length === 0) ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-12 text-center text-sm text-gray-500">{empty}</td>
            </tr>
          ) : rows.map((row, idx) => (
            <tr key={row.id || idx} className="border-b border-gray-800/60 hover:bg-gray-800/40 transition">
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={`px-4 py-3 text-gray-200 ${c.mono ? 'font-mono text-xs' : ''} align-top`}
                >
                  {c.render ? c.render(row[c.key], row) : (row[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// ---- Page ----------------------------------------------------------------
const AdminCompliancePage = () => {
  const navigate = useNavigate();
  const [adminKey, setAdminKey] = useState(null);

  useDocumentMeta({
    title: 'Compliance Footprint | Orosz Coaching Admin',
    description: 'Audit-ready compliance dashboard: elállások, anamnézis, fizetések, Barion Pixel állapot.',
    canonical: '',
    type: 'website',
  });

  useEffect(() => {
    const stored = localStorage.getItem(ADMIN_KEY_STORAGE);
    if (stored) setAdminKey(stored);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_KEY_STORAGE);
    setAdminKey(null);
    navigate('/admin/compliance');
  };

  if (!adminKey) {
    return <LoginScreen onAuth={setAdminKey} />;
  }
  return <Dashboard adminKey={adminKey} onLogout={handleLogout} />;
};

export default AdminCompliancePage;
